import { Product } from '@shared/components/ui/Product';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { useTranslation } from 'react-i18next';
import { StockMovementsModel } from '../../models/StockMovements';
import { Badge } from '@shared/components/ui/badge';
import { UserCard } from '@shared/components/ui/usercard';
import { MovementBatchCell } from '@modules/Reports/components/MovementBatchCell';
import { renderMovementReferences } from './renderShopFromEvent';
import { usePermissions } from '@shared/hooks/usePermissions';

const getDisplayQuantity = (data: StockMovementsModel): number => {
  const qty = data.qty ?? 0;
  if (data.from_type_id === 1 && data.to_type_id !== 1) {
    return -qty;
  }
  return qty;
};

const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export interface CardProps extends RenderItemProps<StockMovementsModel> {
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
}

export const Card = ({ item: stockmovements, className = '' }: CardProps) => {
  const { t } = useTranslation('stockmovementsModule');
  const { getTeamType } = usePermissions();
  const isTenant3pl = getTeamType() === 'tenant_3pl';

  const eventKey = stockmovements.event?.name ?? String(stockmovements.event?.id ?? '');
  const eventName = t(`filters.options.${eventKey}`, {
    defaultValue: eventKey || '-',
  }).toString();

  const displayQty = getDisplayQuantity(stockmovements);
  const formatted = formatDateTime(stockmovements.created_at ?? undefined);

  return (
    <div className={`py-4 px-3 border-b border-lines hover:bg-muted/30 transition-colors ${className}`}>
      <div className="flex flex-col gap-1">
        <div className="border-b border-card-border pb-3">
          <Product
            src={stockmovements.product.image || ''}
            name={stockmovements.product.name}
            id={stockmovements.product.id}
            code={stockmovements.product.code || ''}
            enableLineClamp={false}
            showTooltip={true}
            showImageModal={true}
          />
          <div className="pt-2 pb-1">
            <MovementBatchCell item={stockmovements} i18nNs="stockmovementsModule" />
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-2">
            <Badge variant="outline">{`Cantidad - ${displayQty}`}</Badge>
            <Badge variant="outline" className="text-text">
              {`Total ${stockmovements.total ?? 0}`}
            </Badge>
          </div>
        </div>
        <div className="flex items-start justify-between gap-3 border-b border-card-border pb-1">
          <div className="min-w-0 flex-1">{renderMovementReferences(stockmovements, { showTask: isTenant3pl })}</div>
          <div className="shrink-0 text-sm">{eventName}</div>
        </div>
        <div className="flex items-center justify-between ">
          {isTenant3pl ? (
            stockmovements.user?.name ? (
              <UserCard
                name={stockmovements.user.name}
                email={stockmovements.user.email ?? ''}
                avatarUrl={stockmovements.user.image}
                data-testid={`user-${stockmovements.id}`}
              />
            ) : (
              <span className="text-sm text-complementary-text">{t('fallbacks.no_user')}</span>
            )
          ) : (
            <span />
          )}
          <span className="text-sm">{formatted}</span>
        </div>
      </div>
    </div>
  );
};
